import type { Me, RegisterRequest } from '@sea-wars/protocol';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { ApiError, api } from './client.js';

interface SessionValue {
  readonly user: Me | null;
  readonly loading: boolean;
  readonly signIn: (login: string, password: string) => Promise<void>;
  readonly register: (payload: RegisterRequest) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch((error: unknown) => {
        // 401 przy starcie to normalny stan „nikt nie jest zalogowany", nie awaria.
        if (!(error instanceof ApiError) || error.status !== 401) console.error(error);
      })
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (login: string, password: string) => {
    setUser(await api.login(login, password));
  }, []);

  const register = useCallback(async (payload: RegisterRequest) => {
    setUser(await api.register(payload));
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  return (
    <SessionContext.Provider value={{ user, loading, signIn, register, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession poza SessionProvider');
  return value;
}
